class TimelineWithImage extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelectorAll('.timeline-item');
    this.line = this.querySelector('.timeline-line');
    this.contentSection = this.closest('.timeline-with-image-section__content');
    this.currentActiveIndex = 0;
    this.scrollProgress = 0;
    this.isInTimelineZone = false;
    this.wheelLocked = false;
    this.hasCompleted = false; // Track if timeline has reached 100%
    this.lastScrollY = window.scrollY;

    // Touch-related properties
    this.touchStartY = 0;
    this.touchCurrentY = 0;
    this.isTouching = false;
    this.touchLocked = false;
    this.accumulatedTouchDelta = 0;

    if (this.items.length > 0) {
      this.initTimeline();
      this.setupWheelHandler();
      this.setupTouchHandler();
    }
  }

  initTimeline() {
    // Set first item as active by default
    if (this.items.length > 0) {
      this.items[0].classList.add('active');
      this.updateProgress();
    }

    // Add click handlers (keep for manual interaction)
    this.items.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.setActiveItem(index);
      });
    });
  }

  setupWheelHandler() {
    // Monitor scroll position to check if we're in timeline zone
    this.boundCheckPosition = this.checkPosition.bind(this);
    window.addEventListener('scroll', this.boundCheckPosition, { passive: true });

    // Handle wheel events for controlled progression
    this.boundHandleWheel = this.handleWheel.bind(this);
    window.addEventListener('wheel', this.boundHandleWheel, { passive: false });

    // Prevent scroll events when timeline is active
    this.boundHandleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.boundHandleScroll, { passive: false });
  }

  setupTouchHandler() {
    // Handle touch events for mobile swipe functionality
    this.boundHandleTouchStart = this.handleTouchStart.bind(this);
    this.boundHandleTouchMove = this.handleTouchMove.bind(this);
    this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);

    window.addEventListener('touchstart', this.boundHandleTouchStart, { passive: false });
    window.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false });
    window.addEventListener('touchend', this.boundHandleTouchEnd, { passive: false });
    window.addEventListener('touchcancel', this.boundHandleTouchEnd, { passive: false });
  }

  handleScroll(e) {
    // If timeline is active and not at boundaries, lock scroll position
    if (this.isInTimelineZone && this.lockedScrollY !== undefined) {
      const scrollingDown = window.scrollY > this.lockedScrollY;

      // Keep scroll locked unless at boundaries
      if (!((scrollingDown && this.scrollProgress >= 1) || (!scrollingDown && this.scrollProgress <= 0))) {
        // Force scroll back to locked position
        //window.scrollTo(0, this.lockedScrollY);
      } else {
        // At boundary - update locked position to allow natural scroll
        this.lockedScrollY = window.scrollY;
      }
    }

    this.lastScrollY = window.scrollY;
  }

  checkPosition() {
    if (!this.contentSection) return;

    const contentRect = this.contentSection.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const triggerPointTop = windowHeight * 0.2; // 20% from top
    const triggerPointBottom = windowHeight * 0.8; // 20% from bottom (80% from top)

    const wasInZone = this.isInTimelineZone;

    // Determine if timeline is in active zone
    if (!this.hasCompleted) {
      // Before completion: activate when top reaches 20% from top
      this.isInTimelineZone = contentRect.top <= triggerPointTop && contentRect.bottom > 0;
    } else {
      // After completion: activate when bottom reaches 20% from bottom (scrolling up)
      this.isInTimelineZone = contentRect.bottom >= triggerPointBottom && contentRect.top < windowHeight;
    }

    // Store scroll position when entering timeline zone
    if (this.isInTimelineZone && !wasInZone) {
      this.lockedScrollY = window.scrollY;
    }

    // Reset when scrolling far away (content completely off bottom of screen)
    if (contentRect.top > windowHeight) {
      this.scrollProgress = 0;
      this.hasCompleted = false;
      if (this.currentActiveIndex !== 0) {
        this.setActiveItem(0);
      }
    }
  }

  handleWheel(e) {
    if (!this.isInTimelineZone || this.wheelLocked) return;

    const delta = e.deltaY;
    const scrollingDown = delta > 0;

    // Increment/decrement progress by fixed 10% per wheel (2 scrolls = 20% = 1 item change)
    const progressStep = 0.2; // 10% per scroll, so 2 scrolls = 1 item

    const previousProgress = this.scrollProgress;

    if (scrollingDown) {
      this.scrollProgress = Math.min(1, this.scrollProgress + progressStep);

      // Mark as completed when reaching 100%
      if (this.scrollProgress >= 1) {
        this.hasCompleted = true;
      }
    } else {
      this.scrollProgress = Math.max(0, this.scrollProgress - progressStep);

      // If scrolling back to 0%, clear completion flag
      if (this.scrollProgress <= 0) {
        this.hasCompleted = false;
      }
    }

    // Update the active item
    this.updateActiveItemFromProgress();

    // ALWAYS prevent default scroll while in timeline zone
    // Only allow normal scroll when:
    // - Scrolling down AND already at 100%, OR
    // - Scrolling up AND already at 0%
    if ((scrollingDown && previousProgress >= 1) || (!scrollingDown && previousProgress <= 0)) {
      return; // Allow default scroll behavior only at boundaries
    }

    // Prevent default scroll - lock the section
    e.preventDefault();

    // Lock wheel to prevent rapid firing AFTER we've processed the event
    this.wheelLocked = true;
    setTimeout(() => {
      this.wheelLocked = false;
    }, 100); // 100ms delay between wheel events
  }

  handleTouchStart(e) {
    if (!this.isInTimelineZone) return;

    this.isTouching = true;
    this.touchStartY = e.touches[0].clientY;
    this.touchCurrentY = this.touchStartY;
    this.accumulatedTouchDelta = 0;
  }

  handleTouchMove(e) {
    if (!this.isInTimelineZone || !this.isTouching || this.touchLocked) return;

    const touch = e.touches[0];
    const deltaY = this.touchCurrentY - touch.clientY; // Positive = swipe up, Negative = swipe down
    this.touchCurrentY = touch.clientY;

    // Accumulate touch delta
    this.accumulatedTouchDelta += deltaY;

    // Threshold for triggering progress change (similar to wheel sensitivity)
    const threshold = 50; // 50px swipe equals one progress step

    if (Math.abs(this.accumulatedTouchDelta) >= threshold) {
      const swipingUp = this.accumulatedTouchDelta > 0;
      const progressStep = 0.2; // Same as wheel: 20% per step
      const previousProgress = this.scrollProgress;

      if (swipingUp) {
        this.scrollProgress = Math.min(1, this.scrollProgress + progressStep);

        // Mark as completed when reaching 100%
        if (this.scrollProgress >= 1) {
          this.hasCompleted = true;
        }
      } else {
        this.scrollProgress = Math.max(0, this.scrollProgress - progressStep);

        // If scrolling back to 0%, clear completion flag
        if (this.scrollProgress <= 0) {
          this.hasCompleted = false;
        }
      }

      // Update the active item
      this.updateActiveItemFromProgress();

      // Reset accumulated delta
      this.accumulatedTouchDelta = 0;

      // Check if we should prevent scroll or allow it at boundaries
      const shouldPreventScroll = !((swipingUp && previousProgress >= 1) || (!swipingUp && previousProgress <= 0));

      if (shouldPreventScroll) {
        e.preventDefault();

        // Lock touch to prevent rapid firing
        this.touchLocked = true;
        setTimeout(() => {
          this.touchLocked = false;
        }, 100);
      }
    } else {
      // If still accumulating and not at boundary, prevent scroll
      const swipingUp = deltaY > 0;
      const atBoundary = (swipingUp && this.scrollProgress >= 1) || (!swipingUp && this.scrollProgress <= 0);

      if (!atBoundary) {
        e.preventDefault();
      }
    }
  }

  handleTouchEnd(e) {
    this.isTouching = false;
    this.accumulatedTouchDelta = 0;
  }

  updateActiveItemFromProgress() {
    const itemCount = this.items.length;
    let targetIndex = Math.floor(this.scrollProgress * itemCount);
    targetIndex = Math.max(0, Math.min(targetIndex, itemCount - 1));

    if (targetIndex !== this.currentActiveIndex) {
      this.setActiveItem(targetIndex);
    }
  }

  setActiveItem(index) {
    // Remove active class from all items
    this.items.forEach(item => item.classList.remove('active'));

    // Add active class only to the target item
    this.items[index].classList.add('active');
    this.currentActiveIndex = index;

    // Update progress line
    this.updateProgress();
  }

  updateProgress() {
    if (this.items.length > 1) {
      const progress = (this.currentActiveIndex / (this.items.length - 1)) * 100;
      this.line.style.setProperty('--progress', `${progress}%`);
    }
  }

  disconnectedCallback() {
    // Cleanup event listeners when component is removed
    if (this.boundCheckPosition) {
      window.removeEventListener('scroll', this.boundCheckPosition);
    }
    if (this.boundHandleWheel) {
      window.removeEventListener('wheel', this.boundHandleWheel);
    }
    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll);
    }
    if (this.boundHandleTouchStart) {
      window.removeEventListener('touchstart', this.boundHandleTouchStart);
    }
    if (this.boundHandleTouchMove) {
      window.removeEventListener('touchmove', this.boundHandleTouchMove);
    }
    if (this.boundHandleTouchEnd) {
      window.removeEventListener('touchend', this.boundHandleTouchEnd);
      window.removeEventListener('touchcancel', this.boundHandleTouchEnd);
    }
  }
}

if (!customElements.get('timeline-with-image')) {
  customElements.define('timeline-with-image', TimelineWithImage);
}
